import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type FreshdeskInfo, IMPORT_STATE } from '../types';

export const useFreshdesk = (enable: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.FRESHDESK,
    workspace_alias,
  );

  const { data } = useQuery({
    queryKey: queryKeys,
    queryFn: () => service.freshdesk.getFreshdeskInfo(workspace_alias),
    enabled: enable,
  });

  const { mutate: disconnectFreshdeskMutation } = useMutation({
    mutationFn: () => service.freshdesk.deleteFreshdeskInfo(workspace_alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.freshdesk.importWorkspaceFeedback(workspace_alias, dateRange),
    onSuccess: () => {
      const freshdeskInfo = queryClient.getQueryData(
        queryKeys,
      ) as FreshdeskInfo;
      queryClient.setQueryData(queryKeys, {
        ...freshdeskInfo,
        import_state: IMPORT_STATE.IMPORTING,
      });
    },
  });

  return {
    freshdeskInfo: data,
    disconnectFreshdeskMutation,
    requestImportMutation,
  };
};
